import React from "react";
import { Cpu, Activity, Network, Shield, Lock, Zap, Layers } from "lucide-react";

/**
 * HardwareSpecsCard — displays canonical FortiGate specs from the official
 * Fortinet Product Comparison tool: throughputs, concurrent sessions,
 * new sessions/sec, IPSec VPN, VDOMs, interfaces.
 *
 * Props: hardwareSpecs = { items: [{sku, role, qty, specs}] }
 */
const fmtNumber = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("fr-FR").format(n) : "—";

const fmtGbps = (g) => {
  if (typeof g !== "number") return "—";
  if (g >= 1) return `${g.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Gbps`;
  return `${Math.round(g * 1000)} Mbps`;
};

// Translate the engine role key to a French display + a color theme so the
// header band stands out clearly when comparing several appliances.
const ROLE_THEME = {
  HQ:     { label: "Siège",  bg: "bg-[#D42B1E]",      text: "text-white",     border: "border-[#D42B1E]" },
  Branch: { label: "Agence", bg: "bg-zinc-800",       text: "text-white",     border: "border-zinc-800" },
  Site:   { label: "Site",   bg: "bg-amber-600",      text: "text-white",     border: "border-amber-600" },
};

function roleTheme(role) {
  return ROLE_THEME[role] || { label: role || "—", bg: "bg-zinc-600", text: "text-white", border: "border-zinc-600" };
}

function buildColumnLabels(items) {
  // Number columns sharing the same role so two manual "Site" entries become
  // "Site 1" and "Site 2", three HQs become "Siège 1/2/3", etc.
  const byRole = {};
  return items.map((it) => {
    const t = roleTheme(it.role);
    const sameRoleCount = items.filter((x) => x.role === it.role).length;
    byRole[it.role] = (byRole[it.role] || 0) + 1;
    const numbered = sameRoleCount > 1;
    const label = numbered ? `${t.label} ${byRole[it.role]}` : t.label;
    return { ...t, label };
  });
}

const SpecRow = ({ icon: Icon, label, values, accent = false }) => (
  <tr className={`border-b border-zinc-100 ${accent ? "bg-red-50/40" : ""}`}>
    <td className="px-4 py-2.5 text-zinc-700 align-top">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[#D42B1E]" />
        <span className="text-xs font-medium tracking-wide">{label}</span>
      </div>
    </td>
    {values.map((v, i) => (
      <td key={i} className="px-4 py-2.5 text-right font-mono text-sm text-zinc-900 align-top">
        {v}
      </td>
    ))}
  </tr>
);

export default function HardwareSpecsCard({ hardwareSpecs }) {
  const items = hardwareSpecs?.items || [];
  if (items.length === 0) return null;
  const colLabels = buildColumnLabels(items);

  return (
    <div
      className="border border-zinc-200 bg-white rounded-sm overflow-hidden"
      data-testid="hardware-specs-card"
    >
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500 flex items-center gap-2">
          <Cpu className="h-3.5 w-3.5 text-[#D42B1E]" />
          Spécifications matériel
        </h3>
        <span className="text-xs text-zinc-400 font-mono">
          source : fortinet.com · product-compare
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            {/* Role band — large coloured strip per column to instantly tell
                Siège, Agence, Site-1, Site-2 apart when comparing appliances. */}
            <tr>
              <th className="px-4 py-2 bg-zinc-50" />
              {items.map((it, i) => {
                const c = colLabels[i];
                return (
                  <th
                    key={`role-${it.sku}-${i}`}
                    className={`px-4 py-2 text-center align-middle ${c.bg} ${c.text} border-r border-white/10 last:border-r-0`}
                    data-testid={`hwspec-role-${it.sku}-${i}`}
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] opacity-80">
                      Rôle
                    </div>
                    <div className="text-base font-bold tracking-wide leading-tight">
                      {c.label}
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">
                Caractéristique
              </th>
              {items.map((it, i) => {
                const c = colLabels[i];
                return (
                  <th
                    key={it.sku}
                    className={`px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 min-w-[200px] border-t-4 ${c.border}`}
                    data-testid={`hwspec-col-${it.sku}`}
                  >
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-zinc-900 normal-case text-sm font-semibold">{it.specs.model_name}</span>
                      <span className="text-zinc-500 font-mono text-[10px] normal-case">
                        {it.sku} · ×{it.qty}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <SpecRow icon={Layers} label="Format" values={items.map((it) => it.specs.form_factor || "—")} />
            <SpecRow
              icon={Shield}
              label="Threat Protection"
              values={items.map((it) => fmtGbps(it.specs.threat_protection_throughput_gbps))}
              accent
            />
            <SpecRow
              icon={Zap}
              label="NGFW Throughput"
              values={items.map((it) => fmtGbps(it.specs.ngfw_throughput_gbps))}
            />
            <SpecRow
              icon={Zap}
              label="IPS Throughput"
              values={items.map((it) => fmtGbps(it.specs.ips_throughput_gbps))}
            />
            <SpecRow
              icon={Lock}
              label="SSL Inspection"
              values={items.map((it) => fmtGbps(it.specs.ssl_inspection_throughput_gbps))}
            />
            <SpecRow
              icon={Activity}
              label="Sessions concurrentes"
              values={items.map((it) => fmtNumber(it.specs.concurrent_sessions))}
              accent
            />
            <SpecRow
              icon={Activity}
              label="Nouvelles sessions / s"
              values={items.map((it) => fmtNumber(it.specs.new_sessions_per_sec))}
            />
            <SpecRow
              icon={Lock}
              label="IPSec VPN"
              values={items.map((it) => fmtGbps(it.specs.ipsec_vpn_throughput_gbps))}
            />
            <SpecRow
              icon={Layers}
              label="VDOMs (max)"
              values={items.map((it) => fmtNumber(it.specs.max_vdoms))}
            />
            <SpecRow
              icon={Network}
              label="Interfaces"
              values={items.map((it) => (
                <span className="text-xs whitespace-normal block text-right max-w-[260px] ml-auto">
                  {it.specs.interfaces || "—"}
                </span>
              ))}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}
